import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { FaCalculator, FaDownload, FaEdit, FaInfoCircle, FaMoneyBill, FaRegUser, FaBed, FaCalendarDay, FaReceipt, FaWallet, FaExchangeAlt, FaFileInvoiceDollar, FaCheckCircle } from 'react-icons/fa';
import { LuAlertTriangle } from 'react-icons/lu';
import { formatNumber } from 'helpers/utils';
import { recalculateExpenseBooking } from 'redux/booking/travel/travelBookingSlice';
import { Card, Tabs, ActionDropdown, Confirmation } from 'components/common';
import PersonHoverCard from 'components/common/PersonHoverCard';
// Same icon/colour map the Shuttle form uses, so a "Plane" line matches there.
import { expenseMetaFor } from 'components/app/bookings/travel/booking/expenseIcons';
import BookingUpdateExpenseEmployeeModal from './BookingUpdateExpenseEmployeeModal';

const Row2 = ({ title, hint, extra, children }) => (
  <div className="tw-flex tw-gap-3 tw-mb-3 tw-text-sm">
    <div className="tw-w-1/2">
      <div className="tw-font-bold tw-text-slate-700">{title}</div>
      {hint && <div className="tw-text-xs tw-text-slate-500">{hint}</div>}
      {extra}
    </div>
    <div className="tw-w-1/2 tw-text-blue-700">{children}</div>
  </div>
);
Row2.propTypes = { title: PropTypes.node, hint: PropTypes.node, extra: PropTypes.node, children: PropTypes.node };

// A single expense line: colored icon chip + title/hint on the left, a primary
// value (and optional secondary) right-aligned. Used by the Detail tab.
const Line = ({ icon, iconClass, title, hint, template, value, sub }) => (
  <div className="tw-flex tw-items-start tw-gap-3 tw-py-3 tw-border-b tw-border-slate-100 last:tw-border-0">
    <div className={`tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded tw-text-white tw-bg-gradient-to-br ${iconClass}`}>
      {icon}
    </div>
    <div className="tw-min-w-0 tw-flex-1">
      <div className="tw-font-semibold tw-text-slate-700 tw-text-sm">{title}</div>
      {hint && <div className="tw-text-xs tw-text-slate-400 tw-leading-snug">{hint}</div>}
      {template}
    </div>
    <div className="tw-text-right tw-shrink-0">
      <div className="tw-font-bold tw-text-slate-800 tw-text-sm tw-whitespace-nowrap">{value}</div>
      {sub && <div className="tw-text-xs tw-text-slate-400 tw-whitespace-nowrap">{sub}</div>}
    </div>
  </div>
);
Line.propTypes = {
  icon: PropTypes.node, iconClass: PropTypes.string, title: PropTypes.node,
  hint: PropTypes.node, template: PropTypes.node, value: PropTypes.node, sub: PropTypes.node,
};

// Subtle download link for the expense/advance templates.
const TemplateLink = ({ children }) => (
  <button type="button" className="tw-mt-1.5 tw-inline-flex tw-items-center tw-gap-1 tw-rounded-md tw-bg-blue-50 tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-blue-600 hover:tw-bg-blue-100 tw-border-0 tw-cursor-pointer">
    <FaDownload size={10} /> {children}
  </button>
);
TemplateLink.propTypes = { children: PropTypes.node };

const ExpenseDetail = ({ booking }) => {
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [summaryExpenses, setSummaryExpenses] = useState([]);
  const [totalAmountEstimate, setTotalAmountEstimate] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);

  const currency = booking && booking.currency;
  const employees = booking?.employees || [];
  const dispatch = useDispatch();

  const { allow_update_employee_expense, allow_recalculate_expense } = booking?.permissions || {};

  useEffect(() => {
    if (!booking) return;
    const shuttle = (booking.shuttles || []).map((item) => ({
      name: item.name, description: item.description,
      amount_estimate_currency: item.amount_estimate_currency, amount_currency: item.amount_currency,
    }));
    const hotels = (booking.hotels || []).map((item) => ({
      name: 'Hotel', description: item.name,
      amount_estimate_currency: item.cost_currency, amount_currency: item.cost_currency,
    }));
    const expenses = [...shuttle, ...hotels];
    setSummaryExpenses(expenses);
    setTotalAmountEstimate(expenses.reduce((t, e) => t + Number(e.amount_estimate_currency), 0));
    setTotalAmount(expenses.reduce((t, e) => t + Number(e.amount_currency), 0));
  }, [booking]);

  const followProjectBudget = booking?.follow_project_budget || false;
  // Remaining already accounts for every trip on the job (incl. this one), so
  // it's the single source of truth for the over/under status.
  const overBudgetExpense = Number(booking?.project?.budget_remaining_display) < 0;

  const empActions = [];
  if (allow_update_employee_expense) empActions.push({ label: 'Update employee expenses', icon: <FaEdit />, onClick: () => setShowModal(true) });
  if (allow_recalculate_expense) {
    if (empActions.length) empActions.push({ divider: true });
    empActions.push({ label: 'Recalculate expenses', icon: <FaCalculator />, onClick: () => setShowConfirm(true) });
  }

  const detailTab = (
    <div className="tw-px-1 tw-py-2">
      <Line
        icon={<FaFileInvoiceDollar size={14} />} iconClass="tw-from-primary-500 tw-to-primary-600"
        title="Expense as policy" hint="Expense of employees per day as policy."
        template={<TemplateLink>Travel expense policy template</TemplateLink>}
        value={`${formatNumber(booking.expense_employee_policy, 'VND')} VND`}
        sub={`${formatNumber(booking.expense_employee_policy_currency)} USD`}
      />
      <Line
        icon={<FaCalendarDay size={14} />} iconClass="tw-from-sky-500 tw-to-sky-600"
        title="Number of days" hint="Actual days travel Business as a time of the ticket (plane, train...)"
        value={`${booking.number_of_days} days`}
      />
      <Line
        icon={<FaReceipt size={14} />} iconClass="tw-from-teal-500 tw-to-emerald-600"
        title="Expense of employees for trip" hint="Meal, Transportation & miscellaneous expense"
        value={`${formatNumber(booking.other_cost_currency, currency)} ${currency}`}
      />
      <Line
        icon={<FaWallet size={14} />} iconClass="tw-from-amber-500 tw-to-orange-600"
        title="Cash Advance" hint="Include air ticket expense"
        template={<TemplateLink>Advance Payment Request template</TemplateLink>}
        value={`${formatNumber(booking.advance_cost_currency, currency)} ${currency}`}
      />
      <Line
        icon={<FaExchangeAlt size={13} />} iconClass="tw-from-slate-400 tw-to-slate-500"
        title="Exchange rate" hint="USD exchange rate to VND from Vietcombank at the time of booking."
        value={`1 USD = ${formatNumber(booking.exchange_rate)} VND`}
      />

      <div className="tw-mt-4 tw-flex tw-items-center tw-justify-between tw-rounded-lg tw-bg-gradient-to-r tw-from-slate-800 tw-to-slate-700 tw-px-4 tw-py-3 tw-text-white tw-shadow-sm">
        <span className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-slate-200">
          <FaMoneyBill /> Total expense
        </span>
        <span className="tw-text-lg tw-font-bold tw-whitespace-nowrap">{formatNumber(booking.total_cost_currency, currency)} {currency}</span>
      </div>

      {followProjectBudget && (
        <div className={`tw-mt-3 tw-rounded-lg tw-border tw-overflow-hidden ${overBudgetExpense ? 'tw-border-red-200' : 'tw-border-green-200'}`}>
          {/* Prominent within/over-budget status banner */}
          <div className={`tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2 ${overBudgetExpense ? 'tw-bg-red-50' : 'tw-bg-green-50'}`}>
            <span className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-xl tw-px-2.5 tw-py-1 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide ${overBudgetExpense ? 'tw-bg-red-600 tw-text-white' : 'tw-bg-green-600 tw-text-white'}`}>
              {overBudgetExpense ? <><LuAlertTriangle size={12} /> Over budget</> : <><FaCheckCircle size={12} /> Within budget</>}
            </span>
            {overBudgetExpense && (
              <span className="tw-text-xs tw-font-semibold tw-text-red-600 tw-whitespace-nowrap">
                Over by {formatNumber(Math.abs(Number(booking.project?.budget_remaining_display)), currency)} {currency}
              </span>
            )}
          </div>

          <div className="tw-bg-white tw-px-3 tw-py-2.5">
            <div className="tw-flex tw-items-center tw-justify-between tw-py-1 tw-text-sm">
              <span className="tw-text-slate-500">Project budget</span>
              <span className="tw-font-semibold tw-text-slate-800 tw-whitespace-nowrap">{formatNumber(booking.project?.budget_amount_display, currency)} {currency}</span>
            </div>
            <div className="tw-flex tw-items-center tw-justify-between tw-py-1 tw-text-sm">
              <span className="tw-text-slate-500">Travel expense <span className="tw-text-slate-400">(all trips on job)</span></span>
              <span className="tw-font-semibold tw-text-slate-800 tw-whitespace-nowrap">− {formatNumber(booking.total_expense_by_job_display, currency)} {currency}</span>
            </div>
            <div className="tw-mt-1 tw-flex tw-items-center tw-justify-between tw-border-t tw-border-slate-100 tw-pt-2">
              <span className="tw-text-sm tw-font-semibold tw-text-slate-600">Budget remaining</span>
              <span className={`tw-text-base tw-font-bold tw-whitespace-nowrap ${overBudgetExpense ? 'tw-text-red-600' : 'tw-text-green-600'}`}>{formatNumber(booking.project?.budget_remaining_display, currency)} {currency}</span>
            </div>
            <div className="tw-mt-1 tw-flex tw-items-center tw-justify-between tw-border-t tw-border-slate-100 tw-pt-2 tw-text-sm">
              <span className="tw-text-slate-500">Reimburse to employee</span>
              <span className="tw-font-semibold tw-text-slate-800 tw-whitespace-nowrap">{formatNumber(booking.total_reimburse_cost_currency, currency)} {currency}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const employeesTab = (
    <div className="tw-p-2 tw-text-sm">
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
        <div>Expense of employees</div>
        {empActions.length > 0 && <ActionDropdown items={empActions} menuWidth={220} />}
      </div>
      <Row2 title="Exchange rate" hint="USD exchange rate to VND from Vietcombank at the time of booking."><span className="tw-block tw-text-right">1USD = {formatNumber(booking.exchange_rate)} VND</span></Row2>
      <div className="tw-overflow-x-auto">
        <table className="tw-w-full tw-border-collapse">
          <thead>
            <tr className="tw-bg-slate-100 tw-text-left">
              <th className="tw-px-2 tw-py-2">Name</th>
              <th className="tw-px-2 tw-py-2">Hotel policy</th>
              <th className="tw-px-2 tw-py-2 tw-text-right">Amount currency</th>
              <th className="tw-px-2 tw-py-2 tw-text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {employees.map((emp) => (
              <tr key={emp.id} className="tw-border-b tw-border-slate-100">
                {/* Same identity card as the traveller list on the booking
                    detail: the expense rows are keyed by short codes ("ABZ"),
                    which is not enough to tell whose allowance you are reading. */}
                <td className="tw-px-2 tw-py-2 tw-text-blue-700">
                  <PersonHoverCard person={emp}>
                    <span className="tw-inline-flex tw-items-center tw-gap-1">
                      <FaRegUser size={11} /> {emp.employee_name}
                    </span>
                  </PersonHoverCard>
                </td>
                <td className="tw-px-2 tw-py-2 tw-text-slate-600">
                  {emp.hotel_policy_remark
                    ? <span className="tw-inline-flex tw-items-center tw-gap-1"><FaBed className="tw-text-violet-500" size={11} /> {emp.hotel_policy_remark}</span>
                    : <span className="tw-text-slate-300">—</span>}
                </td>
                <td className="tw-px-2 tw-py-2 tw-text-right">{emp.amount_currency} USD</td>
                <td className="tw-px-2 tw-py-2 tw-text-right">{formatNumber(emp.amount)} VND</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {booking.purpose_of_travel === 'Customer visit factory' && (
        <div className="tw-text-red-600 tw-text-right tw-text-xs tw-mt-2"><FaInfoCircle className="tw-inline tw-mr-1" /> For trips where the purpose is a <strong>customer visit to the factory</strong>, the allowance of sales staff will follow the customer&apos;s actual expense.</div>
      )}
    </div>
  );

  return (
    <>
      <Card
        padding="none"
        className="tw-mb-3"
        header={<h5 className="tw-mb-0 tw-font-semibold tw-text-slate-800 tw-flex tw-items-center tw-gap-2"><FaMoneyBill /> Expense</h5>}
      >
        <div className="tw-bg-slate-50 tw-p-4">
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-6">
            <div>
              <h6 className="tw-text-xs tw-font-bold tw-uppercase tw-tracking-widest tw-text-slate-500 tw-mb-3">Expense summary</h6>
              <div className="tw-overflow-x-auto tw-text-sm">
                <table className="tw-w-full tw-border tw-border-solid tw-border-slate-200 tw-border-collapse">
                  <thead>
                    <tr className="tw-bg-slate-100 tw-text-left">
                      <th className="tw-px-2 tw-py-2">Name</th>
                      <th className="tw-px-2 tw-py-2 tw-text-right">Expense estimate ({currency})</th>
                      <th className="tw-px-2 tw-py-2 tw-text-right">Expense actual ({currency})</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryExpenses.map((item, i) => {
                      const meta = expenseMetaFor(item.name);
                      const Icon = meta.icon;
                      return (
                        <tr key={i} className="tw-border-b tw-border-slate-100">
                          <td className="tw-px-2 tw-py-2">
                            <div className="tw-flex tw-items-center tw-gap-2">
                              <span
                                className="tw-flex tw-h-7 tw-w-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-text-white"
                                style={{ backgroundColor: meta.color }}
                              >
                                <Icon size={12} aria-hidden="true" />
                              </span>
                              <div className="tw-min-w-0">
                                <div className="tw-font-semibold tw-whitespace-nowrap">{item.name}</div>
                                <div className="tw-text-xs">{item.description}</div>
                              </div>
                            </div>
                          </td>
                          <td className="tw-px-2 tw-py-2 tw-text-right">{formatNumber(item.amount_estimate_currency, currency)}</td>
                          <td className="tw-px-2 tw-py-2 tw-text-right">{formatNumber(item.amount_currency, currency)}</td>
                        </tr>
                      );
                    })}
                    <tr className="tw-border-t tw-font-bold">
                      <td className="tw-px-2 tw-py-2 tw-text-right">TOTAL</td>
                      <td className="tw-px-2 tw-py-2 tw-text-right tw-text-blue-700">{formatNumber(totalAmountEstimate, currency)}</td>
                      <td className="tw-px-2 tw-py-2 tw-text-right tw-text-red-600">{formatNumber(totalAmount, currency)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <Tabs
                variant="underline"
                tabs={[
                  { label: 'Detail', content: detailTab },
                  { label: 'Employees', content: employeesTab },
                ]}
              />
            </div>
          </div>
        </div>
      </Card>

      <BookingUpdateExpenseEmployeeModal showModal={showModal} setShowModal={setShowModal} booking={booking} />

      <Confirmation
        show={showConfirm}
        type="warning"
        title="Recalculate expenses"
        message="Are you sure to recalculate expenses for the trip?"
        confirmText="Yes"
        onConfirm={() => { dispatch(recalculateExpenseBooking(booking.id)); setShowConfirm(false); }}
        onCancel={() => setShowConfirm(false)}
      />
    </>
  );
};

ExpenseDetail.propTypes = {
  booking: PropTypes.object,
};

export default ExpenseDetail;
